import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowDownZA,
  ArrowUpAZ,
  ArrowUpDown,
  ChevronDown,
  PlusCircle,
  SlidersHorizontal,
  X,
  XCircleIcon,
  XIcon,
} from 'lucide-react';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import Loader from '../components/Loader';
import Pagination from '../components/shared_components/Pagination';
import PlatformCalendar from '../components/shared_components/platformCalendar';
import { calendarOverrideStyles } from '../components/shared_components/datePickerStyles';
import { useTheme } from '../hooks/useTheme';
import { enterJobFromPicker } from '../store/slices/findCandidatesSlice';
import { getSourcingJobsThunk } from '../store/thunks/FindCandidatesThunk';
import '../css/orgadmin.css';

/** Created On is the job document's `postingDate`, the same field the Job Roles table prints
 *  verbatim as YYYY-MM-DD. Only reformat when it arrives as a full timestamp, and never let
 *  an unparseable value render as "Invalid Date". */
const formatCreatedOn = (value) => {
  if (!value) return 'NA';
  if (typeof value === 'string' && !value.includes('T')) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().split('T')[0];
};

/** An unparseable or missing date sorts as the oldest possible, never as NaN. */
const timeOf = (value) => {
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/** The pipeline summary, one column each. Shortened from the sub-tab labels
 *  ("Saved for Later") to keep the header row narrow. Sourced leads because it's the
 *  only count that isn't zero until the recruiter acts on someone — without it a role
 *  with a full pool reads the same as one never searched. */
const ROLE_STATS = [
  { key: 'sourced_count', label: 'Sourced' },
  { key: 'shortlist_count', label: 'Shortlisted' },
  { key: 'saved_count', label: 'Saved' },
  { key: 'contacted_count', label: 'Contacted' },
];

/**
 * Job-role picker — the first screen of Find Candidates.
 *
 * Every list behind it (sourced, saved, shortlisted, outreach) is scoped to one job role,
 * so the recruiter names that scope here instead of landing on a table already filtered to
 * whichever role happened to be most recent. Choosing one pushes /find-candidates/:jobId,
 * which is where FindCandidates reads its scope from.
 *
 * Deliberately mirrors the Job Roles screen (shared_components/ViewCreatedJDs) down to the
 * chrome — same header, search, filter, table and pagination — but stands on its own file
 * because it answers a different question: which role am I sourcing for, and how far along
 * is each one. Hence the pipeline counts in place of that table's job-type/experience/status
 * columns, and no Download Excel or help video.
 *
 * Fed by getSourcingJobsThunk rather than the JD list, since that thunk is what merges the
 * per-role counts in. It lists every job role the recruiter has, not just the ones already
 * searched under — a role with nothing sourced yet is exactly the one you'd open to start.
 */
function SelectJobRole() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const portal = location.pathname.startsWith('/hr') ? 'hr' : 'org-admin';

  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // null keeps the default order (newest first); the header toggles through A-Z and Z-A.
  const [sortOrder, setSortOrder] = useState(null);

  // Applied vs. temp: the dropdown edits `temp*` and only commits on Apply Now, so a
  // half-picked date range never reshuffles the table underneath the recruiter.
  const [dateRange, setDateRange] = useState([null, null]);
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const filterRef = useRef(null);
  const calendarRef = useRef(null);
  const topRef = useRef(null);

  const jobRoles = useSelector((state) => state.findCandidatesSlice.jobRoles);
  const jobsLoading = useSelector((state) => state.findCandidatesSlice.jobsLoading);

  useEffect(() => {
    // force: the cards on this screen show live shortlisted/contacted/saved counts, so
    // this is the one place that must bypass the thunk's cache guard.
    dispatch(getSourcingJobsThunk({ force: true }));
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !calendarRef.current?.contains(event.target)
      ) {
        setShowFilterDropdown(false);
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredJobs = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();

    const matched = (jobRoles || []).filter((job) => {
      const matchesSearch = (job.job_role || '').toLowerCase().includes(term);

      let matchesDate = true;
      if (dateRange[0] || dateRange[1]) {
        const jobDate = new Date(job.posting_date);
        if (Number.isNaN(jobDate.getTime())) {
          // A role with no usable date can't satisfy a date filter — hiding it is
          // honest, where keeping it would claim a date we don't have.
          matchesDate = false;
        } else {
          const startDate = dateRange[0] ? new Date(dateRange[0]) : null;
          const endDate = dateRange[1] ? new Date(dateRange[1]) : null;
          if (startDate && jobDate < startDate) matchesDate = false;
          if (endDate) {
            endDate.setHours(23, 59, 59, 999);
            if (jobDate > endDate) matchesDate = false;
          }
        }
      }

      return matchesSearch && matchesDate;
    });

    // Until the recruiter sorts: most recently sourced first, since the role you were just
    // sourcing for is the one you're most likely coming back to. Roles never sourced
    // follow, newest created first — the Job Roles table's rule.
    return [...matched].sort((a, b) => {
      const roleA = (a.job_role || '').toLowerCase();
      const roleB = (b.job_role || '').toLowerCase();
      if (!sortOrder) {
        const bySourced = timeOf(b.last_sourced_at) - timeOf(a.last_sourced_at);
        if (bySourced) return bySourced;
        const byCreated = timeOf(b.posting_date) - timeOf(a.posting_date);
        if (byCreated) return byCreated;
        return String(b.job_id).localeCompare(String(a.job_id), undefined, { numeric: true });
      }
      const compared = roleA.localeCompare(roleB);
      return sortOrder === 'A-Z' ? compared : -compared;
    });
  }, [jobRoles, searchQuery, dateRange, sortOrder]);

  const totalItems = filteredJobs.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const currentJobs = filteredJobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Filtering reshuffles the set, so whatever page number was showing no longer means
  // anything. Clamping also covers the list arriving shorter than the page we're on.
  useEffect(() => {
    setCurrentPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSortJobRole = () => {
    setSortOrder((prev) => {
      if (prev === null) return 'A-Z';
      if (prev === 'A-Z') return 'Z-A';
      return null;
    });
  };

  const formatDateRange = (range) => {
    if (!range[0] || !range[1]) return 'Select Date Range';
    return `${range[0].toLocaleDateString()} to ${range[1].toLocaleDateString()}`;
  };

  const handleResetDateRange = () => {
    setTempDateRange([null, null]);
    setDateRange([null, null]);
  };

  const handleResetFilter = () => {
    setShowFilterDropdown(false);
    setSearchQuery('');
    setTempDateRange([null, null]);
    setDateRange([null, null]);
  };

  const handleApplyFilters = () => {
    setDateRange(tempDateRange);
    setShowFilterDropdown(false);
    setCurrentPage(1);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const activeFilters = [];
  if (dateRange[0] || dateRange[1]) {
    activeFilters.push({
      label: `Date: ${formatDateRange(dateRange)}`,
      onRemove: handleResetDateRange,
    });
  }
  const filterCount = activeFilters.length;

  const handleAddJD = () => {
    navigate(`/${portal}/jd-selection`);
  };

  // Seed the store as well as the URL: the header renders the role name from the store, so
  // this keeps it correct even before /sourcing/jobs has resolved on the next screen.
  // Picking a role here also starts the screen clean — see enterJobFromPicker.
  const chooseJob = (job) => {
    const jobRole = job.job_role || 'Untitled role';
    dispatch(enterJobFromPicker({ jobId: job.job_id, jobRole }));
    navigate(`/${portal}/find-candidates/${job.job_id}`, { state: { job_role: jobRole } });
  };

  if (jobsLoading) {
    return <Loader />;
  }

  return (
      <div
        className="min-h-screen flex flex-col border rounded-2xl border-gray-700/50 dark:glass-effect bg-bgSecondary"
        ref={topRef}
      >
        <style>{calendarOverrideStyles(theme)}</style>
        <div className="flex flex-col flex-1 w-full justify-center items-center">
          <div
            className="table-container flex-1 w-full"
            style={{
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              contain: 'layout',
            }}
          >
            <div className="table-header px-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 py-4 mb-4">
                <div className="flex items-center gap-4">
                  <h1 className="text-xl dark:text-[#D9D9D9] text-textPrimary font-normal text-left pl-6">
                    Select a Job you want to source for
                  </h1>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder=" Search by Job Role"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={`w-56  ${theme === 'dark' ? 'glass-effect text-[#EDEDEDE5]' : 'bg-bgPrimary !text-textPrimary'}  border border-gray-600 rounded-full pl-10 pr-4   placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-white text-sm h-10 placeholder:text-sm`}
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        <XIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 dark:text-gray-400 text-textSecondary dark:hover:text-white hover:text-textPrimary cursor-pointer" />
                      </button>
                    )}
                  </div>
                  {portal === 'org-admin' && (
                    <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                      <button
                        onClick={handleAddJD}
                        className="button flex items-center justify-center gap-2 w-full sm:w-auto"
                      >
                        <span className="flex gap-2">
                          <PlusCircle className="w-5 h-5" />
                          <span className="hidden sm:inline">Create Job Role</span>
                          <span className="sm:hidden">Create JD</span>
                        </span>
                      </button>
                    </div>
                  )}

                  <div className="relative" ref={filterRef}>
                    <button
                      className={`flex items-center space-x-4 ${theme === 'dark' ? 'glass-effect ' : 'bg-bgPrimary'} border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light`}
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      <span>Filter</span>
                      {filterCount > 0 && (
                        <>
                          <span className="ml-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs absolute -right-2 -top-1">
                            {filterCount}
                          </span>
                          <XCircleIcon
                            className="w-5 h-5"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResetFilter();
                            }}
                          />
                        </>
                      )}
                    </button>
                    {showFilterDropdown && (
                      <div className="absolute right-0 mt-2 w-[400px] border border-gray-600 rounded-lg shadow-lg z-20 p-6 dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] bg-bgPrimary dark:backdrop-blur-[500px] opacity-100">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-medium dark:text-[#D9D9D9] text-textPrimary">
                            Filter
                          </h3>
                          <button
                            onClick={() => setShowFilterDropdown(false)}
                            className="dark:text-gray-400 dark:hover:text-white text-textPrimary hover:text-textSecondary"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        {activeFilters.length > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                                Active Filters
                              </label>
                              <button
                                onClick={handleResetFilter}
                                className="text-xs text-blue-400 hover:text-blue-300"
                              >
                                Clear All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {activeFilters.map((filter, index) => (
                                <div
                                  key={index}
                                  className="flex items-center dark:bg-gray-700/50 bg-violet-600 dark:text-[#D9D9D9] text-xs px-3 py-1 rounded-full dark:border-green-400 border border-violet-900 text-white"
                                >
                                  <span>{filter.label}</span>
                                  <button
                                    onClick={filter.onRemove}
                                    className="ml-2 dark:text-gray-400 hover:text-white text-gray-200"
                                  >
                                    <XCircleIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Date Range Filter — the one filter this screen's data supports.
                            Job type, experience and status live on the JD list, not on the
                            sourcing job feed, so offering them here would filter on nothing. */}
                        <div className="mb-6">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                              Date Range
                            </label>
                            <button
                              onClick={handleResetDateRange}
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              Reset
                            </button>
                          </div>
                          <div className="relative" ref={calendarRef}>
                            <button
                              className="flex items-center justify-between w-full dark:glass-effect bg-bgPrimary border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textPrimary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light h-10"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsCalendarOpen(!isCalendarOpen);
                              }}
                              aria-expanded={isCalendarOpen}
                            >
                              <span className="truncate">{formatDateRange(tempDateRange)}</span>
                              {tempDateRange?.length === 2 && tempDateRange[0] ? (
                                <X
                                  className="w-4 h-4"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTempDateRange([null, null]);
                                    setIsCalendarOpen(false);
                                  }}
                                />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {isCalendarOpen && (
                              <div
                                className="candidate-analytics-calendar absolute mt-2 z-[100] right-0"
                                style={{
                                  background: theme === 'dark' ? '#0a1733' : '#fff',
                                  border: '1px solid #4b5563',
                                  borderRadius: '8px',
                                }}
                              >
                                <div className={`platform-calendar ${theme}`}>
                                  <PlatformCalendar
                                    onChange={(value) => {
                                      setTempDateRange(value);
                                      if (value[0] && value[1]) {
                                        setIsCalendarOpen(false);
                                      }
                                    }}
                                    value={tempDateRange}
                                    selectRange={true}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={handleResetFilter}
                            className="flex-1 px-4 py-2 border border-gray-600 rounded-full dark:text-gray-300 text-textPrimary dark:hover:bg-gray-700/50 hover:bg-gray-200 text-sm"
                          >
                            Reset All
                          </button>
                          <button
                            onClick={handleApplyFilters}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm hover:opacity-90"
                          >
                            Apply Now
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="w-full overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="w-full flex-1 overflow-y-auto pb-32 min-h-[400px]">
                  <Table
                    className="w-full"
                    style={{
                      transform: 'translateZ(0)',
                      contain: 'layout',
                    }}
                  >
                    <Thead className="border-b border-t border-[#5A4479D4]">
                      <Tr>
                        <Th className="text-left min-w-0">
                          <div className="flex items-center dark:text-[#D8D8D8] text-textSecondary text-xs font-medium normal-case">
                            Job Role
                            <button
                              onClick={handleSortJobRole}
                              className="ml-2 focus:outline-none"
                              aria-label="Sort by job role"
                            >
                              {sortOrder === 'A-Z' ? (
                                <ArrowUpAZ className="w-4 h-4 dark:text-gray-300 text-textSecondary" />
                              ) : sortOrder === 'Z-A' ? (
                                <ArrowDownZA className="w-4 h-4 dark:text-gray-300 text-textSecondary" />
                              ) : (
                                <ArrowUpDown className="w-4 h-4 dark:text-gray-300 text-textSecondary" />
                              )}
                            </button>
                          </div>
                        </Th>
                        <Th className="text-left min-w-0">
                          <span className="dark:text-[#D8D8D8] text-textSecondary text-xs font-medium normal-case">
                            Created On
                          </span>
                        </Th>
                        {ROLE_STATS.map(({ key, label }) => (
                          <Th key={key} className="text-left min-w-0">
                            <span className="dark:text-[#D8D8D8] text-textSecondary text-xs font-medium normal-case">
                              {label}
                            </span>
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {currentJobs.length === 0 && (
                        <Tr>
                          <Td colSpan={2 + ROLE_STATS.length}>
                            <div className="flex h-60 items-center justify-center">
                              <div className="flex flex-col items-center text-center">
                                <h2 className="text-lg font-medium text-gray-400 mb-1">
                                  No Job Role found
                                </h2>
                                <p className="text-sm text-gray-400">
                                  Try adjusting your filters or search terms to see more
                                  results.
                                </p>
                              </div>
                            </div>
                          </Td>
                        </Tr>
                      )}
                      {currentJobs.map((job) => (
                        /* 60px keeps the rows on the same rhythm as the Job Roles table,
                           where the height comes from the Action column's 36px control
                           stack plus the 12px cell padding the shared .table-container td
                           rule applies. Text-only rows would otherwise sit at 44px and read
                           visibly tighter. Only above the responsive table's 40em breakpoint,
                           below which rows stack and need their natural height. */
                        <Tr
                          key={job.job_id}
                          className="group dark:hover:bg-gray-700/20 hover:bg-gray-300/20 cursor-pointer sm:h-[60px]"
                          onClick={() => chooseJob(job)}
                        >
                          <Td className="text-left px-2 py-3 min-w-0 align-middle">
                            <div
                              className="whitespace-normal break-words text-left px-2 dark:text-[#CCCCCCE5] text-textSecondary text-sm"
                              title={job.job_role}
                            >
                              {job.job_role}
                            </div>
                          </Td>
                          <Td className="text-left px-2 py-3 min-w-0 align-middle">
                            <div className="truncate text-left px-2 dark:text-[#CCCCCCE5] text-textSecondary text-sm">
                              {formatCreatedOn(job.posting_date)}
                            </div>
                          </Td>
                          {/* Where the role stands, so the pipeline is readable before
                              opening it. Zeros included — a blank cell would read as
                              "not loaded" rather than "nothing here yet". */}
                          {ROLE_STATS.map(({ key }) => (
                            <Td key={key} className="text-left px-2 py-3 min-w-0 align-middle">
                              <div className="truncate text-left px-2 dark:text-[#CCCCCCE5] text-textSecondary text-sm">
                                {job[key] ?? 0}
                              </div>
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={(size) => {
                    setItemsPerPage(size);
                    setCurrentPage(1);
                  }}
                  label="Jobs"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default SelectJobRole;
